/**
 * CR500 Phase 2B: Modal Presentation Mode
 * 
 * Renders the deal card with modal dialog for details.
 * Scaffolded - full implementation renders same blocks as flip.
 */

import React from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Deal } from "@/lib/types/deals";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import type { 
  DetailsEntryConfig, 
  ModalPresentationConfig,
} from "@/lib/layout-studio/detailsPresentationTypes";
import { DEFAULT_DETAILS_ENTRY, DEFAULT_MODAL_CONFIG } from "@/lib/layout-studio/detailsPresentationTypes";
import { getDealCardTitle, getDealCardCondition } from "@/lib/deals/dealCardDisplay";
import { DetailsBlocksRenderer } from "./DetailsBlocksRenderer";
import { cn } from "@/lib/utils";

// ================================================================
// TYPES
// ================================================================

export interface ModalPresentationProps {
  /** The deal being displayed */
  deal: Deal & { id?: string };
  /** The resolved v2 spec */
  spec: ResolvedDealCardSpecV2;
  /** Admin context */
  isAdmin?: boolean;
  /** Whether modal is open */
  isOpen: boolean;
  /** Open modal handler */
  onOpen: () => void;
  /** Close modal handler */
  onClose: () => void;
  /** Modal configuration */
  config?: ModalPresentationConfig;
  /** Entry button configuration */
  entryConfig?: DetailsEntryConfig;
  /** The card content */
  children: React.ReactNode;
}

// Size class mapping
const SIZE_CLASSES: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

// ================================================================
// COMPONENT
// ================================================================

export function ModalPresentation({
  deal,
  spec,
  isAdmin = false,
  isOpen,
  onOpen,
  onClose,
  config = DEFAULT_MODAL_CONFIG,
  entryConfig = DEFAULT_DETAILS_ENTRY,
  children,
}: ModalPresentationProps) {
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen();
  };
  
  return (
    <>
      {/* Card with trigger */}
      <div className="relative">
        {children}
        
        {/* Entry button */}
        <button
          onClick={handleTriggerClick}
          className={cn(
            "absolute z-10 px-3 py-1.5 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
            entryConfig.variant === "primary" && "bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary",
            entryConfig.variant === "secondary" && "bg-secondary text-secondary-foreground hover:bg-secondary/90 focus:ring-secondary",
            entryConfig.variant === "ghost" && "bg-background/80 text-foreground hover:bg-muted focus:ring-muted",
            entryConfig.position === "bottom-right" && "bottom-3 right-3",
            entryConfig.position === "bottom-left" && "bottom-3 left-3",
            entryConfig.position === "bottom-center" && "bottom-3 left-1/2 -translate-x-1/2",
          )}
          aria-label={`Show ${deal.id || "deal"} details`}
        >
          {entryConfig.label}
        </button>
      </div>
      
      {/* Modal dialog */}
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent 
          className={cn(
            "p-0 gap-0",
            SIZE_CLASSES[config.size] || SIZE_CLASSES.md,
          )}
          onInteractOutside={(e) => {
            if (!config.closeOnBackdropClick) {
              e.preventDefault();
            }
          }}
        >
          {/* Header */}
          <DialogHeader className="p-4 border-b bg-muted/30">
            <DialogTitle className="text-base font-semibold truncate pr-8">
              {getDealCardTitle(deal)}
            </DialogTitle>
            {getDealCardCondition(deal) && (
              <p className="text-sm text-muted-foreground truncate">
                {getDealCardCondition(deal)}
              </p>
            )}
          </DialogHeader>
          
          {/* Content - v2 blocks */}
          <div className="max-h-[60vh] overflow-y-auto p-4">
            <DetailsBlocksRenderer
              deal={deal}
              spec={spec}
              isAdmin={isAdmin}
              mode="modal"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ModalPresentation;
